import { useQuery, DocumentNode, TypedDocumentNode, OperationVariables } from '@apollo/client';
import { DataGridProProps, GridRowModel, GridEventListener } from '@mui/x-data-grid-pro';
import { toObject } from '@procureai/query-builder';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useRef, useMemo, useCallback } from 'react';
import isEqual from 'react-fast-compare';

import { addInternalRowIds } from '../../util/internalRowIds';

const PAGE_SIZE = 100;

interface UseDataGridInfiniteLoadingProps<TData = any, TVariables extends OperationVariables = OperationVariables> {
  skip?: boolean;
  query: DocumentNode | TypedDocumentNode<TData, TVariables>;
  variables?: Omit<TVariables, 'limit' | 'offset'>;
  getNodes: (data: TData) => GridRowModel[];
  getTotalCount: (data: TData) => number | undefined | null;
}

type UseDataGridInfiniteLoadingResult = Pick<
  DataGridProProps,
  | 'rows'
  | 'onRowsScrollEnd'
  | 'loading'
  | 'rowCount'
  | 'scrollEndThreshold'
  | 'sortingMode'
  | 'filterMode'
  | 'rowsLoadingMode'
>;

export const useDataGridInfiniteLoading = <TData = any, TVariables extends OperationVariables = OperationVariables>({
  query,
  variables,
  skip,
  getNodes,
  getTotalCount,
}: UseDataGridInfiniteLoadingProps<TData, TVariables>): UseDataGridInfiniteLoadingResult => {
  const variablesRef = useRef(variables);
  if (!isEqual(variablesRef.current, variables)) {
    variablesRef.current = variables;
  }
  const { onError } = useCustomSnackbar();

  const { filter } = (variables ?? {}) as any;
  const { count: filterCount, group: filteredGroup } = useMemo(() => toObject(filter), [filter]);

  const { data, fetchMore, loading } = useQuery<TData, TVariables>(query, {
    skip,
    onError,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      ...variablesRef.current,
      filter: filterCount ? filteredGroup : undefined,
      limit: PAGE_SIZE,
      offset: 0,
    } as any,
  });

  const result = useMemo(() => {
    if (data) {
      return {
        rows: addInternalRowIds(getNodes(data)),
        rowCount: getTotalCount(data),
      };
    }
    return { rows: [], rowCount: undefined };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const rowCount = result.rowCount ?? undefined;

  // Fetch rows as they become visible in the viewport
  const onRowsScrollEnd: GridEventListener<'rowsScrollEnd'> = useCallback(
    async (params) => {
      const offset = params.visibleRowsCount;

      if (params.viewportPageSize && rowCount !== undefined && offset < rowCount) {
        try {
          await fetchMore({
            variables: {
              limit: PAGE_SIZE,
              offset,
            } as any,
          });
        } catch (e) {
          onError(e);
        }
      }
    },
    [fetchMore, onError, rowCount],
  );

  return {
    rows: result.rows,
    rowCount,
    loading,
    onRowsScrollEnd,
    sortingMode: 'server',
    filterMode: 'server',
    rowsLoadingMode: 'client',
  };
};
